import instantsearch from "instantsearch.js";
import {
  index,
  pagination,
  searchBox,
  hits,
  configure,
} from "instantsearch.js/es/widgets";

import SITE_LANGUAGE from "../partials/siteLanguage";
import contentIndex from "../partials/contentIndex";
import { translate } from "../partials/translate";
import { 
	algoliaIndexPrefix, 
	searchClient 
} from "../partials/ais/client";

import { chevronDown, pdf, external } from "../partials/ais/svgs";
import { 
	defaultPurchaseOptions, 
	friendlyPurchaseOptionLabel 
} from "../partials/ais/generate-fund-codes";
import {
	buildAllFundCodes,
	getPrimaryFundId
} from "../partials/ais/fund-listing-utils";
import { fundFacts, kypReport } from "../partials/fundLinks";

const searchInput =
  document.querySelector(".js-search-input");

const resultsFundsPagination = document.querySelector(
		".js-funds-pagination"
	);
const resultsContentPagination =
  document.querySelector(".js-content-pagination");
const resultsExpertsPagination = document.querySelector(
  ".js-experts-pagination"
);
const resultsBriefcasePagination = document.querySelector(
  ".js-briefcase-pagination"
);

const fundsResults =
  document.querySelector(".js-funds-results");
const contentResults = document.querySelector(
  ".js-content-results"
);
const expertsResults = document.querySelector(
  ".js-experts-results"
);
const briefcaseResults = document.querySelector(
  ".js-briefcase-results"
);

const searchResults = () => {
  if (
    !(resultsFundsPagination instanceof HTMLElement) ||
    !(resultsContentPagination instanceof HTMLElement) ||
    !(resultsExpertsPagination instanceof HTMLElement) ||
    !(resultsBriefcasePagination instanceof HTMLElement)
  ) {
    return;
  }

  if (!(searchInput instanceof HTMLElement)) {
    return;
  }

  if (
    !(fundsResults instanceof HTMLElement) ||
    !(contentResults instanceof HTMLElement) ||
    !(expertsResults instanceof HTMLElement) ||
    !(briefcaseResults instanceof HTMLElement)
  ) {
    return;
  }

	const fundIndex = `${algoliaIndexPrefix}_fundfamilies_${SITE_LANGUAGE}`;

  const search = instantsearch({
    indexName: contentIndex,
    searchClient,
		future: {
			preserveSharedStateOnUnmount: false,
		},
    routing: {
      stateMapping: {
        stateToRoute(uiState) {
          const indexUiState = uiState[contentIndex];
          return {
            q: indexUiState.query,
          };
        },
        routeToState(routeState) {
          return {
            [contentIndex]: {
              query: routeState.q,
            },
          };
        },
      },
    },
  });

  const configureOptions = {
    hitsPerPage: 5,
    ignorePlurals: true,
  };

  const paginationOptions = {
    showFirst: false,
    showPrevious: true,
    showNext: true,
    showLast: false,
    padding: 1,
    templates: {
      next(pagination, {html}) {
				return html`${translate("Next")}`;
			},
      previous(pagination, {html}) {
				return html`${translate("Prev")}`;
			}
    },
  };

  const searchbox = searchBox({
    container: searchInput,
    searchAsYouType: false,
    cssClasses: {
      form: "results__search-box__form",
      input: "results__search-box__input",
      submit: "results__search-box__submit",
      submitIcon: "results__search-box__submit-icon",
      reset: "results__search-box__reset",
      resetIcon: "results__search-box__reset-icon",
    },
  });

  search.addWidgets([
    searchbox,
		configure({
			facetFilters: ["section:articles", `siteLanguage:${SITE_LANGUAGE}`],
			distinct: true,
			...configureOptions,
		}),
		hits({
			container: contentResults,
			templates: {
				empty(hits, {html}) {
					return html`<p>${translate('No results')}</p>`;
				},
				item(hit, {html}) {
					const isSameDomain = hit.url.startsWith(window.location.origin);
					return html`
					<article class="pb-2 mb-4 border-b border-grayBrand-keyline">
						<div>
								<span class="block font-medium text-12 capitalize">${hit.categories.includes('Videos') ? 'Videos' : 'Articles'}</span>
							<a href="${hit.url}" class="mb-1 font-bold text-14 hover:text-blueBrand" target="${isSameDomain ? '_self' : '_blank'}" rel="${isSameDomain ? '' : 'noopener noreferrer'}">
								<span>${hit.title}</span>
							</a>

							<div class="text-12">
								${hit.referenceDescription && html`<p>${hit.referenceDescription.replace(/(<([^>]+)>)/gi, "")}</p>`}
							</div>
						</div>
					</article>
					`;
				},
			},
		}),
		pagination({
			container: resultsContentPagination,
			scrollTo: false,
			...paginationOptions,
		}),
    index({
      indexId: "experts",
      indexName: contentIndex,
    }).addWidgets([
      configure({
        facetFilters: [["section:specialists", "section:authors", "section:internalSalesRep"], `siteLanguage:${SITE_LANGUAGE}`],
				distinct: false,
        ...configureOptions,
      }),
      hits({
        container: expertsResults,
        templates: {
					empty() {
						return '';
					},
          item(hit, {html}) {
						return html`<article class="pb-2 mb-4 border-b border-grayBrand-keyline">
							<div>
								<a href="${hit.url}" class="mb-1 font-bold text-14 hover:text-blueBrand">
									<span>${hit.title}</span>
								</a>

								<div class="text-12 search-results__item__body">
									${hit.referenceDescription && html`<p>${hit.referenceDescription.replace(/(<([^>]+)>)/gi, "")}</p>`}
									${hit.body && html`<p>${hit.body}</p>`}
								</div>
							</div>
						</article>
						`;
					},
        },
      }),
      pagination({
        container: resultsExpertsPagination,
        scrollTo: false,
        ...paginationOptions,
      }),
    ]),
    index({
      indexId: "briefcase",
      indexName: contentIndex,
    }).addWidgets([
      configure({
        facetFilters: ["section:reports", `siteLanguage:${SITE_LANGUAGE}`],
				distinct: false,
        ...configureOptions,
      }),
      hits({
        container: briefcaseResults,
        templates: {
					empty() {
						return '';
					},
          item(hit, {html}) {
						return html`<article class="pb-2 mb-4 border-b border-grayBrand-keyline">
							<div>
								<a href="${hit.url}" class="mb-1 font-bold text-14 hover:text-blueBrand" target="_blank">
									<span>${hit.title}</span>
								</a>
							</div>
						</article>
						`;
					},
        },
      }),
      pagination({
        container: resultsBriefcasePagination,
        scrollTo: false,
        ...paginationOptions,
      }),
    ]),
    index({
      indexId: "funds",
      indexName: fundIndex,
    }).addWidgets([
      configure({
        facetFilters: [],
				distinct: false,
        ...configureOptions,
      }),
      hits({
        container: fundsResults,
        templates: {
					empty(hits, {html}) {
						return html`${translate('No results')}`;
					},
          item(hit, { html, components }) {
						const primaryFundId = getPrimaryFundId(hit);
						return`
						<div class="py-4">
							<a href="/fund/${primaryFundId}" class="block">
								<strong>${hit.name}</strong>
							</a>
							<details class="fund-details cursor-pointer js-details">
								<summary class="flex flex-row text-xs text-grayBrand-details js-summary">
									${translate('Fund Details')}
									${chevronDown}
								</summary>
								<div class="flex flex-row mt-4 gap-4 lg:gap-12 text-sm">
									<div class="flex flex-col">
										<span class="text-xs font-bold mb-2">${translate('Fund Codes')}</span>
										
										${defaultPurchaseOptions(hit.purchaseOptions, hit.name).map(option => {
											return (
												`<div class="px-3 py-1 mb-1 mr-2 border-2 border-blackBrand hover:border-blueBrand hover:text-blueBrand text-14">
													<a class="inline-block" href="/fund/${option.fundId}">
														${friendlyPurchaseOptionLabel(option)}
													</a>
												</div>
												`
										)}).join(' ')}
									</div>
									<div>
										<span class="list-none text-xs font-bold my-2">${translate('All Fund Codes')}</span>	
										${hit.purchaseOptions.map(option => {
											return `${buildAllFundCodes(option)}`
										}).join(' ')}										
									</div>
									<ul class="mt-1 text-xs flex flex-col items-end text-right">
										<span class="font-bold mb-1">${translate('More Information')}</span>
										<li>
											<a href="https://bmomf.lipperweb.com/bmomf/Pdf/?symbol=${primaryFundId}&lang=${SITE_LANGUAGE}" class="hover:text-blueBrand flex flex-row my-1">
												${translate('Fund Profile (PDF)')}
												${pdf}
											</a>
										</li>
										<li>
											<a href="${fundFacts(hit.objectID)}" class="hover:text-blueBrand flex flex-row my-1">
												${translate('Fund Facts')}
												${pdf}
											</a>										
										</li>
										<li>
											<a href="/fund/${primaryFundId}#under-the-hood" class="hover:text-blueBrand flex flex-row my-1">
												${translate('Top Holdings')}
												${external}
											</a>
										</li>
										${kypReport(hit.objectID) ? (
											`<li>
													<a href="${kypReport(hit.objectID)}" class="hover:text-blueBrand flex flex-row my-1">
													${translate('KYP Report')}
													</a>	
											</li>`
										) : ''}
									</ul>
								</div>
							</details>
						</div>
						`
					},
        },
      }),
      pagination({
        container: resultsFundsPagination,
        scrollTo: false,
        ...paginationOptions,
      }),
    ]),
  ]);

  search.start();

  search.on("render", () => {
    if (fundsResults.innerText != translate("No results")) {
			resultsFundsPagination.classList.remove("hidden");
    } else {
			resultsFundsPagination.classList.add("hidden");
		}
    if (contentResults.innerText != translate("No results")) {
			resultsContentPagination.classList.remove("hidden");
    } else {
			resultsContentPagination.classList.add("hidden");
		}
		if (expertsResults.innerText) {
			expertsResults.closest('.search-results__section')?.classList.remove("hidden");
			resultsExpertsPagination.classList.remove("hidden");
		} else {
			expertsResults.closest('.search-results__section')?.classList.add("hidden");
			resultsExpertsPagination.classList.add("hidden");
		}
		if (briefcaseResults.innerText) {
			briefcaseResults.closest('.search-results__section')?.classList.remove("hidden");
			resultsBriefcasePagination.classList.remove("hidden");
		} else {
			briefcaseResults.closest('.search-results__section')?.classList.add("hidden");
			resultsBriefcasePagination.classList.add("hidden");
		}
  });
};

export default searchResults();
